/**
 * Map raw RTK Query errors from qduels-api into a small enum the friends-list
 * UI can switch on, so the row component stays free of magic status codes and
 * error-string literals.
 *
 * qduels-api 4xx errors carry `{ error: "<code>" }`. The known codes are the
 * business cases (defender_not_linked, challenger_not_linked,
 * cannot_challenge_self) plus the auth cases (missing_bearer_token,
 * invalid_token). Anything else falls through to `unknown`.
 */

import type { QduelsErrorBody, QduelsErrorCode } from '@/types/qduels';

export type QduelsErrorKind =
  | 'defender_not_linked'
  | 'challenger_not_linked'
  | 'cannot_challenge_self'
  | 'auth'
  | 'network'
  | 'server'
  | 'unknown';

// Business codes whose string value IS a QduelsErrorKind (returned verbatim).
const CHALLENGE_BUSINESS_CODES = [
  'defender_not_linked',
  'challenger_not_linked',
  'cannot_challenge_self',
] as const;
type ChallengeBusinessCode = (typeof CHALLENGE_BUSINESS_CODES)[number];

// Auth codes map to the 'auth' bucket rather than passing through verbatim.
const AUTH_CODES = new Set<QduelsErrorCode>(['missing_bearer_token', 'invalid_token']);

function isChallengeBusinessCode(code: string): code is ChallengeBusinessCode {
  return (CHALLENGE_BUSINESS_CODES as readonly string[]).includes(code);
}

function isQduelsErrorBody(data: unknown): data is QduelsErrorBody {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { error?: unknown }).error === 'string'
  );
}

export function classifyQduelsError(err: unknown): QduelsErrorKind {
  const e = err as { status?: number | string; data?: unknown };

  if (e?.status === 'FETCH_ERROR' || e?.status === 'TIMEOUT_ERROR') {
    return 'network';
  }

  // Prefer the server-provided `error` code when present — qduels returns a
  // specific business code with HTTP 400, and the auth codes with 401.
  if (isQduelsErrorBody(e?.data)) {
    const code = e.data.error;
    if (AUTH_CODES.has(code as QduelsErrorCode)) return 'auth';
    if (isChallengeBusinessCode(code)) return code;
  }

  if (e?.status === 401) return 'auth';
  if (typeof e?.status === 'number' && e.status >= 500) return 'server';
  return 'unknown';
}

/**
 * True when an RTK Query error from `GET /v1/players/me` means "the caller has
 * no linked Clash Royale account yet" — qduels answers 404 with
 * `{ error: "no_linked_player" }`. Anything else (401 auth, network, 5xx) is a
 * genuine failure and must NOT be treated as "not linked", or we'd block the
 * Sfida button on a transient hiccup.
 */
export function isNotLinkedError(err: unknown): boolean {
  const e = err as { status?: number | string; data?: unknown };
  if (e?.status === 404) return true;
  if (isQduelsErrorBody(e?.data) && e.data.error === 'no_linked_player') {
    return true;
  }
  return false;
}

/** Classification of a verify-token failure into UI copy buckets. */
export type QduelsVerifyErrorKind =
  | 'invalid_token' // 422 { verified: false } — wrong/expired in-game token
  | 'already_linked' // 409 player_tag_already_linked
  | 'invalid_player_tag' // 400 invalid_player_tag
  | 'auth'
  | 'network'
  | 'server'
  | 'unknown';

/**
 * Map a `POST /v1/players/verify-token` failure to a UI bucket. The 422 case
 * carries `{ verified: false }` (no `error` code), so we key off the status
 * code there; the 409/400 cases carry the documented `{ error }` codes.
 */
export function classifyQduelsVerifyError(err: unknown): QduelsVerifyErrorKind {
  const e = err as { status?: number | string; data?: unknown };

  if (e?.status === 'FETCH_ERROR' || e?.status === 'TIMEOUT_ERROR') {
    return 'network';
  }

  if (isQduelsErrorBody(e?.data)) {
    const code = e.data.error;
    if (code === 'player_tag_already_linked') return 'already_linked';
    if (code === 'invalid_player_tag') return 'invalid_player_tag';
    if (code === 'missing_bearer_token' || code === 'invalid_token') {
      return 'auth';
    }
  }

  // 422 is the "token wrong/expired" case; qduels returns `{ verified: false }`
  // there with no `error` string, so the body check above won't catch it.
  if (e?.status === 422) return 'invalid_token';
  if (e?.status === 400) return 'invalid_player_tag';
  if (e?.status === 409) return 'already_linked';
  if (e?.status === 401) return 'auth';
  if (typeof e?.status === 'number' && e.status >= 500) return 'server';
  return 'unknown';
}

/** Classification of a `POST /v1/queue/enter` failure into UI copy buckets. */
export type QduelsQueueErrorKind =
  | 'player_not_linked' // 400 player_not_linked — caller has no linked CR account
  | 'not_eligible' // 403 not_eligible — CR account below the floor
  | 'already_in_match' // 409 already_in_match — caller already has a live match
  | 'auth'
  | 'network'
  | 'server'
  | 'unknown';

/**
 * Map a `POST /v1/queue/enter` failure to a UI bucket. The three business cases
 * carry their documented `{ error }` codes (with 400/403/409 respectively); we
 * prefer the code and fall back to the status so a missing/garbled body still
 * routes to the right CTA.
 */
export function classifyQduelsQueueError(err: unknown): QduelsQueueErrorKind {
  const e = err as { status?: number | string; data?: unknown };

  if (e?.status === 'FETCH_ERROR' || e?.status === 'TIMEOUT_ERROR') {
    return 'network';
  }

  if (isQduelsErrorBody(e?.data)) {
    const code = e.data.error;
    if (code === 'player_not_linked') return 'player_not_linked';
    if (code === 'not_eligible') return 'not_eligible';
    if (code === 'already_in_match') return 'already_in_match';
    if (code === 'missing_bearer_token' || code === 'invalid_token') {
      return 'auth';
    }
  }

  if (e?.status === 400) return 'player_not_linked';
  if (e?.status === 403) return 'not_eligible';
  if (e?.status === 409) return 'already_in_match';
  if (e?.status === 401) return 'auth';
  if (typeof e?.status === 'number' && e.status >= 500) return 'server';
  return 'unknown';
}

/** Classification of a tournament join/leave failure. */
export type QduelsJoinErrorKind =
  | 'player_not_linked' // 400 — no linked CR account
  | 'not_eligible' // 403 — below the live cards/level floor
  | 'tournament_full' // 409
  | 'already_joined' // 409
  | 'not_joined' // 409 — leave: you are not in this tournament (any more)
  | 'closed' // 409 registration_closed / cannot_leave_<status>
  | 'cr_upstream' // Supercell is unhappy: TRANSIENT, not "tournament gone"
  | 'rate_limited' // 429
  | 'not_found' // 404 tournament_not_found
  | 'auth'
  | 'network'
  | 'server'
  | 'unknown';

// Supercell-origin failures surfaced through join. `cr_player_not_found` is a
// 404 that reads like "the tournament is missing" — it is not; it means CR
// could not resolve the player right now. Bucketed as transient on purpose.
const CR_UPSTREAM_CODES = new Set<string>([
  'cr_player_not_found',
  'cr_rate_limited',
  'cr_upstream_error',
]);

/**
 * Map a tournament join/leave failure to a UI bucket.
 *
 * Codes are checked BEFORE status, because the same status means different
 * things here: 404 is `tournament_not_found` (real) or `cr_player_not_found`
 * (transient), and 409 covers full / already-joined / not-joined / closed.
 *
 * ⚠️ Join does NOT follow the `cannot_<verb>_<status>` template that
 * leave/start/cancel use — it is the one lifecycle verb that doesn't. Joining a
 * tournament past registration returns **409 `registration_closed`**. Getting
 * this wrong is not academic: a sit&go auto-starts the INSTANT it fills, so
 * "tapped Join a moment too late" is the single most likely failure on this
 * screen.
 */
export function classifyQduelsJoinError(err: unknown): QduelsJoinErrorKind {
  const e = err as { status?: number | string; data?: unknown };

  if (e?.status === 'FETCH_ERROR' || e?.status === 'TIMEOUT_ERROR') return 'network';

  if (isQduelsErrorBody(e?.data)) {
    const code = e.data.error;
    if (code === 'missing_bearer_token' || code === 'invalid_token') return 'auth';
    if (CR_UPSTREAM_CODES.has(code)) return 'cr_upstream';
    if (code === 'player_not_linked') return 'player_not_linked';
    if (code === 'not_eligible') return 'not_eligible';
    if (code === 'tournament_full') return 'tournament_full';
    if (code === 'already_joined') return 'already_joined';
    if (code === 'not_joined') return 'not_joined';
    if (code === 'registration_closed') return 'closed';
    if (code === 'tournament_not_found') return 'not_found';
    if (code === 'rate_limited') return 'rate_limited';
    // Leave/start/cancel DO use the status-templated form
    // (`cannot_leave_in_progress`, …): match by PREFIX, since an equality check
    // breaks the moment a status we haven't seen appears.
    if (code.startsWith('cannot_leave_')) return 'closed';
  }

  if (e?.status === 401) return 'auth';
  if (e?.status === 429) return 'rate_limited';
  if (typeof e?.status === 'number' && e.status >= 500) return 'server';
  return 'unknown';
}
